import { bibleReadingPlan, type DayOfWeek, type DailyReading } from '@/data/bible-reading-plan';

export interface TodaysReading {
  week: number;
  day: DayOfWeek;
  dayLabel: string;
  passages: string[];
  dateFormatted: string;
  isBeforePlanStart: boolean;
  isAfterPlanEnd: boolean;
}

const DAY_LABELS: Record<DayOfWeek, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

const DAY_INDEX_TO_NAME: DayOfWeek[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

// Plan starts January 1, 2026 (Thursday)
const PLAN_START = new Date('2026-01-01T00:00:00');
// Plan ends around December 31, 2026 (Week 53, Thursday)
const PLAN_END = new Date('2026-12-31T23:59:59');

/**
 * Gets the current date/time in Toronto Eastern Time
 */
function getTorontoDate(): Date {
  const now = new Date();
  const torontoString = now.toLocaleString('en-US', {
    timeZone: 'America/Toronto',
  });
  return new Date(torontoString);
}

/**
 * Calculates the week number (1-53) based on the date
 * Week 1 starts on January 1, 2026 (Thursday)
 * Weeks are Sunday-based after Week 1
 */
function getWeekNumber(date: Date): number {
  const startOfYear = new Date(2026, 0, 1); // January 1, 2026

  // Calculate days since start of year
  const diffTime = date.getTime() - startOfYear.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Week 1 is Jan 1-3 (Thu-Sat), then Week 2 starts Sunday Jan 4
  if (diffDays < 3) {
    return 1; // Thursday (0), Friday (1), Saturday (2) = Week 1
  }

  // After that, weeks start on Sunday
  // Jan 4 (Sunday) is day index 3, which should be Week 2
  // (diffDays - 3) / 7 gives us weeks since Week 2 started
  const weeksAfterFirst = Math.floor((diffDays - 3) / 7);
  return 2 + weeksAfterFirst;
}

/**
 * Gets today's Bible reading based on Toronto Eastern Time
 */
export function getTodaysReading(): TodaysReading {
  const torontoDate = getTorontoDate();
  const dayOfWeek = torontoDate.getDay(); // 0 = Sunday, 6 = Saturday
  const day = DAY_INDEX_TO_NAME[dayOfWeek];

  // Format the date nicely
  const dateFormatted = torontoDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Toronto',
  });

  // Check if before plan starts
  if (torontoDate < PLAN_START) {
    return {
      week: 0,
      day,
      dayLabel: DAY_LABELS[day],
      passages: [],
      dateFormatted,
      isBeforePlanStart: true,
      isAfterPlanEnd: false,
    };
  }

  // Check if after plan ends
  if (torontoDate > PLAN_END) {
    return {
      week: 53,
      day,
      dayLabel: DAY_LABELS[day],
      passages: [],
      dateFormatted,
      isBeforePlanStart: false,
      isAfterPlanEnd: true,
    };
  }

  const weekNumber = getWeekNumber(torontoDate);

  // Find the week in our plan
  const weekData = bibleReadingPlan.find((w) => w.week === weekNumber);

  if (!weekData) {
    return {
      week: weekNumber,
      day,
      dayLabel: DAY_LABELS[day],
      passages: [],
      dateFormatted,
      isBeforePlanStart: false,
      isAfterPlanEnd: false,
    };
  }

  // Find today's reading
  const todaysReading = weekData.readings.find((r) => r.day === day);

  return {
    week: weekNumber,
    day,
    dayLabel: DAY_LABELS[day],
    passages: todaysReading?.passages || [],
    dateFormatted,
    isBeforePlanStart: false,
    isAfterPlanEnd: false,
  };
}

/**
 * Gets a specific day's reading for a given week
 */
export function getReadingForDay(week: number, day: DayOfWeek): DailyReading | null {
  const weekData = bibleReadingPlan.find((w) => w.week === week);
  if (!weekData) return null;

  return weekData.readings.find((r) => r.day === day) || null;
}
